// react_frontend/src/Components/Modules/ProductSetting/EditProduct.js
import React, { useState, useRef, useContext, useEffect } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { MotherContext } from "../../../Context/MotherContext";
// RSuite Components
import {
  Form,
  Button,
  Message,
  Loader,
  Whisper,
  Tooltip,
  Schema,
  SelectPicker,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";
// Icons
import {
  FaBox,
  FaTags,
  FaRuler,
  FaDollarSign,
  FaClipboardList,
  FaPencilAlt,
} from "react-icons/fa";

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    categories,
    items,
    updateProductInList,
  } = useContext(MotherContext);

  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef();

  // ✅ Fetch product data only
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await AxiosAuthInstance.get(`add_product/${id}`);
        const product = response.data.product;
        setFormValue({
          id: product.id,
          category_id: product.category_id?.toString(),
          item_id: product.item_id?.toString(),
          product_code: product.product_code,
          product_unit: product.product_unit,
          product_unit_price: product.product_unit_price,
        });
      } catch (error) {
        console.error("Failed to load product:", error);

        const errors = error.response?.data?.errors || {};
        let errorMsg = "Failed to update product.";

        if (errors.item_id) {
          errorMsg = "Item is already taken.";
        } else if (errors.product_code) {
          errorMsg = "Product code is already taken.";
        } else if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        }

        toaster.push(
          <Message type="error" duration={5000}>
            {errorMsg || 'Failed to load product data.'}
          </Message>,
          { placement: "topEnd" }
        );
        navigate("/product_list");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  // ✅ Filter active categories & items
  const activeCategories = categories.filter(cat => cat.category_status === 1).sort((a, b) => a.category_name.localeCompare(b.category_name));
  const activeItems = items.filter(item => item.item_status === 1).sort((a, b) => a.item_name.localeCompare(b.item_name));

  // ✅ Filter items by selected category
  const filteredItems = formValue.category_id
    ? activeItems.filter(item => item.category_id.toString() === formValue.category_id)
    : [];

  // Handle Category Change
  const handleCategoryChange = (value) => {
    setFormValue((prev) => ({
      ...prev,
      category_id: value,
      item_id: "", // Reset item
    }));
  };

  // Validation Schema
  const { StringType, NumberType } = Schema.Types;
  const model = Schema.Model({
    category_id: StringType().isRequired("Category is required."),
    item_id: StringType().isRequired("Item is required."),
    product_code: StringType().isRequired("Product code is required."),
    product_unit: StringType().isRequired("Unit is required."),
    product_unit_price: NumberType()
      .min(0, "Price cannot be negative.")
      .isRequired("Unit price is required."),
  });

  // Handle Submit
  const handleSubmit = async () => {
    if (!formRef.current.check()) return;
    setLoading(true);
    try {
      const payload = {
        id: formValue.id,
        category_id: formValue.category_id,
        item_id: formValue.item_id,
        product_code: formValue.product_code,
        product_unit: formValue.product_unit,
        product_unit_price: formValue.product_unit_price,
      };
      const res = await AxiosAuthInstance.put(`add_product/${id}`, payload);
      if (res.data.status === true) {
        toaster.push(
          <Message type="success" duration={3000}>
            Product updated successfully!
          </Message>,
          { placement: "topEnd" }
        );
        if (res.data.product) {
          updateProductInList(res.data.product);
        }
        navigate("/product_list");
      } else {
        toaster.push(
          <Message type="error" duration={5000}>
            {res.data.message || "Update failed."}
          </Message>,
          { placement: "topEnd" }
        );
      }
    } catch (err) {
      console.error("Update error:", err);
      const errorMsg =
        err.response?.data?.message ||
        Object.values(err.response?.data?.errors || {})[0]?.[0] ||
        "Failed to update product.";
      toaster.push(
        <Message type="error" duration={5000}>
          {errorMsg}
        </Message>,
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/product_list");
  };

  // Handle Item Change
  const handleItemChange = (value) => {
    setFormValue((prev) => {
      const selectedItem = items.find(item => item.id.toString() === value);
      const unit = selectedItem?.product_unit || "";
      return {
        ...prev,
        item_id: value,
        product_unit: unit,
      };
    });
  };

  return (
    <>
      <Breadcrumb title="Edit Product" />
      <div className="p-3">
        <div
          className="card shadow-sm border-0 mx-auto"
          style={{ maxWidth: "700px" }}
        >
          {/* Header */}
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#4351A3", color: "white" }}
          >
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <FaPencilAlt /> Edit Product
            </h5>
            <Link to="/product_list" className="text-white text-decoration-none">
              <Whisper placement="left" speaker={<Tooltip>Product List</Tooltip>}>
                <Button size="sm" appearance="subtle" className="text-white">
                  <FaClipboardList /> List
                </Button>
              </Whisper>
            </Link>
          </div>
          {/* Body */}
          <div className="card-body bg-light p-4">
            <Form
              ref={formRef}
              fluid
              formValue={formValue}
              formError={formError}
              onChange={setFormValue}
              onCheck={setFormError}
              model={model}
            >
              {/* Category */}
              <Form.Group controlId="category_id">
                <Form.ControlLabel>Category *</Form.ControlLabel>
                <Form.Control
                  name="category_id"
                  accepter={SelectPicker}
                  data={activeCategories.map((cat) => ({
                    label: (
                      <div className="d-flex align-items-center gap-2">
                        <FaTags /> <span>{cat.category_name}</span>
                      </div>
                    ),
                    value: cat.id.toString(),
                  }))}
                  valueKey="value"
                  labelKey="label"
                  placeholder="Select Category"
                  style={{ width: "100%" }}
                  renderMenuOnBody
                  cleanable={false}
                  onChange={handleCategoryChange}
                />
                <Form.ErrorMessage name="category_id" />
              </Form.Group>
              {/* Item Name */}
              <Form.Group controlId="item_id">
                <Form.ControlLabel>Item Name *</Form.ControlLabel>
                <Form.Control
                  name="item_id"
                  accepter={SelectPicker}
                  data={filteredItems.map((item) => ({
                    label: (
                      <div className="d-flex align-items-center gap-2">
                        <FaBox /> <span>{item.item_name}</span>
                      </div>
                    ),
                    value: item.id.toString(),
                  }))}
                  valueKey="value"
                  labelKey="label"
                  placeholder="Select Item"
                  style={{ width: "100%" }}
                  renderMenuOnBody
                  disabled={!formValue.category_id}
                  cleanable={false}
                  onChange={handleItemChange}
                />
                <Form.ErrorMessage name="item_id" />
              </Form.Group>
              {/* Unit → READONLY */}
              <Form.Group controlId="product_unit">
                <Form.ControlLabel>Unit *</Form.ControlLabel>
                <Form.Control
                  name="product_unit"
                  value={formValue.product_unit}
                  readOnly // ✅
                  style={{
                    backgroundColor: "#f8f9fa",
                    cursor: "not-allowed",
                  }}
                  prefix={<FaRuler />}
                />
                <Form.ErrorMessage name="product_unit" />
              </Form.Group>

              {/* Product Code */}
              <Form.Group controlId="product_code">
                <Form.ControlLabel>Product Code *</Form.ControlLabel>
                <Form.Control
                  name="product_code"
                  placeholder="e.g., PRD-001"
                  prefix={<FaBox />}
                />
                <Form.ErrorMessage name="product_code" />
              </Form.Group>

              {/* Unit Price */}
              <Form.Group controlId="product_unit_price">
                <Form.ControlLabel>Unit Price * (This price represents your selling amount.)</Form.ControlLabel>
                <Form.Control
                  name="product_unit_price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g., 100.00"
                  prefix={<FaDollarSign />}
                />
                <Form.ErrorMessage name="product_unit_price" />
              </Form.Group>
              {/* Action Buttons */}
              <div className="d-flex gap-3 pt-3 border-top" style={{ marginTop: "2rem" }}>
                <Button
                  appearance="primary"
                  color="blue"
                  size="md"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? (
                    <>
                      <Loader size="sm" /> Updating...
                    </>
                  ) : (
                    "Update Product"
                  )}
                </Button>
                <Button
                  appearance="subtle"
                  color="red"
                  size="md"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{
                    flex: 1,
                    border: "1px solid #dc3545",
                    color: "#dc3545",
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProduct;